/**
 * Sort Values Configuration
 *
 * Defines sort options for ElasticSearch queries.
 * With ES, sorting is done via query parameters instead of separate indices.
 *
 * Reference: maple/frontend/strat/strat/search/sortValues/sortByValues.ts
 * Index settings: maple/backend/mustang/mustang/search/elastic/ads_index.py
 *
 * IMPORTANT: The ES index must have sort settings configured:
 * 'sort': {
 *     'field': ['productScore', 'score', 'createdAt', 'externalID'],
 *     'order': ['asc', 'desc', 'desc', 'asc'],
 * }
 */

/**
 * Sort option keys
 */
export const SortKeys = {
  POPULAR: "popular",
  DEFAULT: "default",
  DATE_DESC: "date_desc",
  PRICE_ASC: "price_asc",
  PRICE_DESC: "price_desc",
}

/**
 * Sort options with their ES sort attributes
 *
 * Each sort option has:
 * - key: The sort key used in URLs/params
 * - label: Display label
 * - attributes: Array of sort attributes for ES query
 *   - name: Field name (or "_script" for script-based sorting)
 *   - order: 'asc' or 'desc'
 *   - script: (optional) Script for custom sorting logic
 *
 * Algolia's customRanking for Mustang:
 *   ['asc(productScore)', 'desc(score)', 'desc(createdAt)', 'asc(externalID)']
 *
 * NOTE: For externalID tie-breaking, we use a script sort to convert the Keyword field to
 * numeric value, matching Algolia's numeric sorting behavior. Without this, ES would sort
 * lexicographically ("10" < "2" instead of 2 < 10).
 *
 * Reference:
 * - maple/backend/strat/strat/classifieds/search/algolia/ads_index.py (INDEX_REPLICAS)
 * - maple/backend/mustang/mustang/settings/common_overrides.py (STRAT_SEARCH_CUSTOM_RANKING)
 */
export const SORT_OPTIONS = {
  [SortKeys.POPULAR]: {
    key: SortKeys.POPULAR,
    label: "Popular",
    // Matches Algolia: productScore ASC (paid ads first), score DESC, createdAt DESC, externalID ASC (numeric)
    attributes: [
      { name: "productScore", order: "asc" },
      { name: "score", order: "desc" },
      { name: "createdAt", order: "desc" },
      { name: "_script", order: "asc", script: { type: "number", script: "Long.parseLong(doc['externalID'].value)" } },
    ],
  },
  [SortKeys.DEFAULT]: {
    key: SortKeys.DEFAULT,
    label: "Popular",
    // Default uses same sorting as popular
    attributes: [
      { name: "productScore", order: "asc" },
      { name: "score", order: "desc" },
      { name: "createdAt", order: "desc" },
      { name: "_script", order: "asc", script: { type: "number", script: "Long.parseLong(doc['externalID'].value)" } },
    ],
  },
  [SortKeys.DATE_DESC]: {
    key: SortKeys.DATE_DESC,
    label: "Newest",
    // Primary: createdAt DESC, then customRanking chain for equal createdAt values
    attributes: [
      { name: "createdAt", order: "desc" },
      { name: "productScore", order: "asc" },
      { name: "score", order: "desc" },
      { name: "_script", order: "asc", script: { type: "number", script: "Long.parseLong(doc['externalID'].value)" } },
    ],
  },
  [SortKeys.PRICE_ASC]: {
    key: SortKeys.PRICE_ASC,
    label: "Lowest Price",
    // Primary: price ASC, then full customRanking chain for equal prices
    attributes: [
      { name: "price", order: "asc" },
      { name: "productScore", order: "asc" },
      { name: "score", order: "desc" },
      { name: "createdAt", order: "desc" },
      { name: "_script", order: "asc", script: { type: "number", script: "Long.parseLong(doc['externalID'].value)" } },
    ],
  },
  [SortKeys.PRICE_DESC]: {
    key: SortKeys.PRICE_DESC,
    label: "Highest Price",
    // Primary: price DESC, then full customRanking chain for equal prices
    attributes: [
      { name: "price", order: "desc" },
      { name: "productScore", order: "asc" },
      { name: "score", order: "desc" },
      { name: "createdAt", order: "desc" },
      { name: "_script", order: "asc", script: { type: "number", script: "Long.parseLong(doc['externalID'].value)" } },
    ],
  },
}

/**
 * Get sort option by key
 * @param {string} key - Sort key
 * @returns {Object|null} Sort option or null if not found
 */
export const getSortForKey = (key) => {
  if (!key) {
    return SORT_OPTIONS[SortKeys.POPULAR]
  }

  const normalizedKey = key.toLowerCase()
  return SORT_OPTIONS[normalizedKey] || SORT_OPTIONS[SortKeys.POPULAR]
}

/**
 * Get sort attributes array for ES query
 * @param {string} key - Sort key
 * @returns {Array} Array of sort attributes
 */
export const getSortAttributes = (key) => {
  const sortOption = getSortForKey(key)
  return sortOption?.attributes || []
}

/**
 * Validate if a sort key is valid
 * @param {string} key - Sort key to validate
 * @returns {boolean} True if valid
 */
export const isValidSortKey = (key) => {
  if (!key) return true // null/undefined defaults to popular
  return Object.keys(SORT_OPTIONS).includes(key.toLowerCase())
}

/**
 * Get all available sort options
 * @returns {Array} Array of sort options with key and label
 */
export const getAllSortOptions = () => {
  return Object.values(SORT_OPTIONS)
    .filter((opt) => opt.key !== SortKeys.DEFAULT) // Exclude default as it's same as popular
    .map(({ key, label }) => ({ key, label }))
}

export default SORT_OPTIONS
